import { Link, Outlet } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import corvusLogo from '@/assets/corvus-logo.png';
import { SkipLink } from '@/components/skip-link/skip-link';
import { apiClient } from '@/modules/api/api-client';
import { queryKeys } from '@/modules/query/query-keys';
import { useAuthStore } from '@/stores/auth-store';

import { formatBadgeCount, NavBar } from './nav-bar';

/**
 * Application layout component with a full-width top header and sidebar navigation.
 * 
 * Layout structure:
 * ┌─────────────────────────────────────────┐
 * │  Header (logo + branding ... user info) │
 * ├──────────┬──────────────────────────────┤
 * │ Sidebar  │  Content (Outlet)            │
 * │  (nav)   │                              │
 * └──────────┴──────────────────────────────┘
 * 
 * Landmark regions (Requirement 17.7):
 * - <header> (banner): Full-width top bar with logo and user info
 * - <nav> (navigation): Sidebar navigation
 * - <main> (main): Page content area (provided by each page component via id="main-content")
 */
export function AppLayout() {
  const user = useAuthStore((state) => state.user);

  const { data: notificationData } = useQuery<{ count: number }>({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: async () => {
      try {
        return await apiClient.get<{ count: number }>('/notifications/unread-count');
      } catch {
        return { count: 0 };
      }
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const unreadCount = notificationData?.count ?? 0;

  const badgeText = formatBadgeCount(unreadCount);

  return (
    <div className="flex h-screen flex-col bg-surface-raised overflow-hidden">
      <SkipLink />
      <header className="flex-shrink-0 flex items-center gap-4 border-b border-border bg-surface px-6 py-3">
        <img
          src={corvusLogo}
          alt=""
          className="h-16 w-auto ml-10 lg:ml-0"
          aria-hidden="true"
        />
        <div>
          <h1 className="text-lg font-semibold text-content">Corvus</h1>
          <p className="text-xs text-content-tertiary">Intelligent CI/CD Platform</p>
        </div>

        {/* Notifications + User info — right side of header */}
        <div className="ml-auto flex items-center gap-4">

          {user && (
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-content">{user.displayName ?? 'Unknown user'}</p>
                <p className="text-xs text-content-tertiary">{user.role}</p>
              </div>
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200 text-sm font-semibold"
                aria-hidden="true"
              >
                {(user.displayName?.charAt(0) ?? '?').toUpperCase()}
              </div>
            </div>
          )}
          <Link
            to="/notifications"
            className="relative rounded-md p-2 text-content-secondary hover:text-content hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 transition-colors"
            aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {badgeText && (
              <span
                className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-accent-600 px-1.5 py-0.5 text-[10px] font-bold text-white min-w-[18px]"
                aria-hidden="true"
              >
                {badgeText}
              </span>
            )}
          </Link>
        </div>
      </header>
      <div className="flex flex-1 overflow-hidden">
        <NavBar />
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
